import { invalidateExploreSummary } from '../explore/exploreRefresh';
import React, { useEffect, useRef, useState } from 'react';
import { Link, useLocation, useNavigate, useParams } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Helmet } from 'react-helmet-async';
import {
  FiChevronLeft,
  FiChevronRight,
  FiHeart,
  FiMessageCircle,
  FiBookmark,
  FiShare,
  FiEdit2,
  FiTrash2,
  FiFlag,
} from 'react-icons/fi';
import { reviewsApi } from '../../api/reviews';
import { useAlbumDetail, useAlbumDetailBySlug } from './hooks/useAlbumDetail';
import { useUserStore } from '../../stores/userStore';
import { useWebLocale } from '../../contexts/WebLocaleContext';
import { useColorScheme } from '../../contexts/ColorSchemeContext';
import { reviewRoutePaths } from '../../utils/reviewRoutePaths';
import { saveCurrentPathAndRedirectToLogin } from '../../utils/authUtils';
import { shareOrCopyUrl } from '../../utils/share';
import { SITE_URL } from '../../utils/env';
import {
  useRememberContent,
  recentReview,
  recentScope,
  removeRecent,
} from '../../features/recent/recentContentStore';
import baseline, { appTextStyle } from '../../app-ui/baseline';
import PageLayout from '../../components/layout/PageLayout';
import ProfileIdentity from '../../components/content/ProfileIdentity';
import SEO from '../../components/common/SEO';
import OpenInAppBanner from '../../components/common/OpenInAppBanner';
import AlbumLoadState from './components/album/AlbumLoadState';
import AlbumActionEditor, { RatingStar } from './components/album/AlbumActionEditor';
import AlbumActionConfirm from './components/album/AlbumActionConfirm';
import { saveAlbumReview, deleteAlbumReview } from './components/album/albumActionModel';
import { MemberAlbumSummary } from './components/user-review/MemberReviewListParts';
import ReviewComments, { ReviewAvatar } from './components/detail/ReviewComments';
import ReviewSafetyDialog from './components/detail/ReviewSafetyDialog';
import {
  ReviewDraftProvider,
  useReviewDraft,
  useReleaseReviewDrafts,
} from './components/detail/ReviewDraftProtection';
import useTrackPageView from '../../hooks/useTrackPageView';
import { PAGE_TYPE, CONTENT_TYPE } from '../../constants/gaEnums';
import { trackContentLike, trackShareClick } from '../../lib/gaEvents';
import { useBrowseSession, useBrowsePosition } from '../../features/browse/useBrowseList';
import { DetailReturnContext } from './components/detail/DetailReturnState';
import './MemberReviews.css';
import './UserReviewDetail.css';

export default function UserReview() {
  const memory = useBrowseSession('individual-review');
  const user = useUserStore((s) => s.user),
    { locale } = useWebLocale();
  return (
    <ReviewDraftProvider
      key={`${memory.location.key}:${memory.entry.version}`}
      en={locale === 'en'}
    >
      <DetailReturnContext.Provider value={memory}>
        <ReviewDetailPage user={user} {...memory} />
      </DetailReturnContext.Provider>
    </ReviewDraftProvider>
  );
}
export function ReviewDetailPage({ user, entry, returning }) {
  const releaseDrafts = useReleaseReviewDrafts();
  const params = useParams(),
    location = useLocation(),
    navigate = useNavigate(),
    client = useQueryClient();
  const routes = reviewRoutePaths(location.pathname, location.search),
    reviewId = Number(params.reviewId);
  const slug = useAlbumDetailBySlug(params.albumSlug),
    direct = useAlbumDetail(params.albumSource, params.sourceId),
    albumQuery = params.albumSlug ? slug : direct,
    album = albumQuery.data;
  const query = useQuery({
      queryKey: ['review', reviewId, user?.id ?? 'guest'],
      enabled: Number.isSafeInteger(reviewId) && reviewId > 0,
      queryFn: async () => {
        const r = await reviewsApi.getReviewById(reviewId);
        return r.data;
      },
    }),
    review = query.data;
  const { locale } = useWebLocale(),
    en = locale === 'en',
    { resolvedMode } = useColorScheme(),
    colors = baseline.tokens[resolvedMode === 'dark' ? 'darkColors' : 'lightColors'];
  const [editing, setEditing] = useState(false),
    [confirm, setConfirm] = useState(false),
    [busy, setBusy] = useState(null),
    [error, setError] = useState(false),
    [notice, setNotice] = useState(''),
    [safety, setSafety] = useState(null),
    [commentCount, setCommentCount] = useState(null),
    [commentsLoading, setCommentsLoading] = useState(true),
    [compact, setCompact] = useState(false),
    [editorState, setEditorState] = useState({ dirty: false, busy: false });
  const mounted = useRef(true),
    pending = useRef(false),
    deleted = useRef(false),
    commentInput = useRef(null);
  useReviewDraft(editorState.dirty, Boolean(busy) || editorState.busy, en);
  useEffect(() => {
    mounted.current = true;
    const onScroll = () => setCompact((v) => window.scrollY > (v ? 44 : 56));
    window.addEventListener('scroll', onScroll, { passive: true });
    onScroll();
    return () => {
      mounted.current = false;
      window.removeEventListener('scroll', onScroll);
    };
  }, []);
  const assertAccount = () => {
    if (!mounted.current || !user || String(useUserStore.getState().user?.id) !== String(user.id))
      throw new Error('Inactive account or route');
  };
  const own = Boolean(user && review?.user && String(user.id) === String(review.user.id)),
    canDelete = own || user?.role === 'ADMIN';
  const detailPath = routes.detail(reviewId),
    ready = Boolean(album && review?.user);
  const position = useBrowsePosition({ entry, returning, ready: ready && !commentsLoading });
  const initialComment = useRef(returning && entry.y !== undefined ? undefined : params.commentId);
  const missing =
    [404, 410].includes(query.error?.response?.status) || (query.isSuccess && !review);
  useEffect(() => {
    if (!deleted.current && missing && album) navigate(routes.album, { replace: true });
  }, [missing, album, navigate, routes.album]);
  const recent = recentReview(review, album);
  useRememberContent(recent ? { ...recent, path: detailPath } : null, {
    ready,
    error: query.error,
    path: detailPath,
  });
  useTrackPageView(
    {
      pageType: PAGE_TYPE.REVIEWS_DETAIL,
      contentType: CONTENT_TYPE.REVIEW,
      contentId: reviewId,
      contentName: album?.title,
    },
    { ready },
  );
  const refresh = async () => {
    await invalidateExploreSummary(client);
    await Promise.all(
      [
        'review',
        'albumReviews',
        'albumDetail',
        'reviewsAlbums',
        'browse-reviews',
        'myBookmarks',
        'album-review-history',
        'me',
      ].map((key) => client.invalidateQueries({ queryKey: [key] })),
    );
  };
  const login = () => saveCurrentPathAndRedirectToLogin(navigate);
  const run = async (kind, operation) => {
    if (!user) {
      login();
      return;
    }
    if (pending.current) return;
    pending.current = true;
    setBusy(kind);
    setError(false);
    try {
      assertAccount();
      await operation();
      if (!deleted.current) {
        assertAccount();
        await refresh();
      }
    } catch {
      if (mounted.current) setError(true);
    } finally {
      pending.current = false;
      if (mounted.current) setBusy(null);
    }
  };
  const remove = async () => {
    assertAccount();
    const persisted = { ...album, id: review.albumId ?? album.id };
    await deleteAlbumReview(persisted, review);
    assertAccount();
    deleted.current = true;
    removeRecent(recentScope(user.id), (i) => i.id === `review:${review.id}`);
    setConfirm(false);
    void refresh();
    releaseDrafts();
    navigate(routes.album, { replace: true });
  };
  const save = async (draft) => {
    assertAccount();
    await saveAlbumReview({ ...album, id: review.albumId ?? album.id }, review, draft, 'review');
    assertAccount();
    await refresh();
  };
  const share = async () => {
    if (pending.current) return;
    pending.current = true;
    setBusy('share');
    try {
      const result = await shareOrCopyUrl({
        url: detailPath,
        event: {
          reviewId: review.id,
          albumId: review.albumId ?? album.id,
          surface: 'REVIEW_DETAIL',
          section: 'SHARE',
        },
        title: `${album.title} — ${review.user.nickname}`,
        text: en
          ? `${review.user.nickname}'s review of ${album.title}`
          : `${review.user.nickname}님의 ${album.title} 리뷰`,
      });
      if (mounted.current) {
        if (result === 'copied') setNotice(en ? 'Link copied.' : '링크가 복사되었습니다.');
        if (result !== 'cancelled')
          trackShareClick(
            {
              contentType: CONTENT_TYPE.REVIEW,
              contentId: review.id,
              contentName: album.title,
              pageType: PAGE_TYPE.REVIEWS_DETAIL,
            },
            user,
          );
      }
    } catch {
      if (mounted.current) setError(true);
    } finally {
      pending.current = false;
      if (mounted.current) setBusy(null);
    }
  };
  const count = commentCount ?? review?.comments?.length ?? 0;
  return (
    <PageLayout paddingTop={0}>
      <div
        {...position}
        className="pp-member-reviews pp-review-detail"
        style={{
          '--member-section-gap': `${baseline.tokens.spacing.lg}px`,
          '--member-grid-row-gap': `${baseline.tokens.spacing.xl}px`,
          '--member-text': colors.text,
          '--member-secondary': colors.textSecondary,
          '--member-body': colors.reviewBody,
          '--member-divider': colors.divider,
          '--member-muted': colors.surfaceMuted,
        }}
      >
        <div className="pp-member-toolbar">
          <button
            aria-label={en ? 'Back' : '뒤로가기'}
            onClick={() => (window.history.state?.idx > 0 ? navigate(-1) : navigate(routes.album))}
          >
            <FiChevronLeft size={22} />
          </button>
          <h1>
            {compact && ready ? (
              <>
                <span>
                  {en ? `${review.user.nickname}'s review` : `${review.user.nickname}님의 리뷰`}
                </span>
                <small>{album.title}</small>
              </>
            ) : en ? (
              'Review'
            ) : (
              '리뷰'
            )}
          </h1>
        </div>
        <AlbumLoadState
          album={ready ? album : null}
          loading={albumQuery.isLoading || query.isLoading}
          error={
            query.error ||
            albumQuery.error ||
            (!Number.isSafeInteger(reviewId) || reviewId <= 0
              ? new Error('Invalid route')
              : query.isSuccess && !review?.user
                ? new Error('Invalid review')
                : null)
          }
          onRetry={() => {
            void query.refetch();
            void (albumQuery.retry ?? albumQuery.refetch)?.();
          }}
        >
          {ready && (
            <>
              <OpenInAppBanner
                type="review"
                title={`${review.user.nickname}님의 리뷰`}
                subtitle={`${album.artist?.name ?? ''} · ${album.title}`}
                image={album.imageUrl}
              />
              <SEO
                title={`${album.artist?.name ?? ''} - ${album.title} 리뷰 by ${review.user.nickname}`}
                description={review.content?.slice(0, 160)}
                image={album.imageUrl}
                url={`${SITE_URL}${detailPath}`}
              />
              <Helmet>
                <script type="application/ld+json">
                  {JSON.stringify({
                    '@context': 'https://schema.org',
                    '@type': 'Review',
                    name: `${album.title} review by ${review.user.nickname}`,
                    reviewBody: review.content,
                    datePublished: review.createdAt?.replace(
                      /(\d+)\.(\d+)\.(\d+)/,
                      (_, y, m, d) => `${y}-${m.padStart(2, '0')}-${d.padStart(2, '0')}`,
                    ),
                    author: {
                      '@type': 'Person',
                      name: review.user.nickname,
                      url: `${SITE_URL}/users/${encodeURIComponent(review.user.nickname)}`,
                    },
                    itemReviewed: {
                      '@type': 'MusicAlbum',
                      name: album.title,
                      byArtist: { '@type': 'MusicGroup', name: album.artist?.name },
                      image: album.imageUrl,
                    },
                    reviewRating: {
                      '@type': 'Rating',
                      ratingValue: review.rating,
                      bestRating: 5,
                      worstRating: 0,
                    },
                  })}
                </script>
                <script type="application/ld+json">
                  {JSON.stringify({
                    '@context': 'https://schema.org',
                    '@type': 'BreadcrumbList',
                    itemListElement: [
                      ['Postpop', ''],
                      ['Reviews', '/reviews'],
                      [album.title, routes.album],
                    ].map(([name, path], i) => ({
                      '@type': 'ListItem',
                      position: i + 1,
                      name,
                      item: `${SITE_URL}${path}`,
                    })),
                  })}
                </script>
              </Helmet>
              <MemberAlbumSummary album={album} to={routes.album} en={en} />
              <div className="pp-member-divider" />
              <section className="pp-review-body-section">
                <Link
                  className="pp-review-author"
                  to={`/users/${encodeURIComponent(review.user.nickname)}`}
                  aria-label={`${review.user.nickname} ${en ? 'profile' : '프로필 보기'}`}
                >
                  <ReviewAvatar user={review.user} />
                  <ProfileIdentity
                    name={review.user.nickname}
                    mark={review.user.profileMark}
                    avatar={false}
                  />
                  <span
                    className="pp-review-score"
                    aria-label={`${en ? 'Rating' : '평점'} ${Number(review.rating).toFixed(1)}`}
                    style={appTextStyle(baseline.contentText.score)}
                  >
                    <RatingStar value={1} size={16} />
                    {Number(review.rating).toFixed(1)}
                  </span>
                </Link>
                {review.isHistorical && (
                  <p className="pp-review-detail-meta">
                    {en
                      ? 'Earlier record · excluded from the current average.'
                      : '이전 기록 · 현재 평균에 포함되지 않습니다.'}
                  </p>
                )}
                <p className="pp-review-body" style={appTextStyle(baseline.contentText.body)}>
                  {review.content?.trim()}
                </p>
                <div className="pp-review-stats-owner">
                  <p className="pp-review-detail-meta">
                    {en ? 'Likes' : '좋아요'} {review.likeCount ?? 0} · {en ? 'Comments' : '댓글'}{' '}
                    {count} · {en ? 'Saved' : '저장'} {review.bookmarkCount ?? 0}
                    {review.createdAt
                      ? ` · ${review.createdAt.slice(0, 10).replaceAll('-', '.')}`
                      : ''}
                  </p>
                  <div className="pp-review-owner-actions">
                    {own && !review.isHistorical && (
                      <button onClick={() => setEditing(true)} disabled={Boolean(busy)}>
                        <FiEdit2 size={13} />
                        {en ? 'Edit' : '수정'}
                      </button>
                    )}
                    {canDelete ? (
                      <button
                        className="is-danger"
                        onClick={() => setConfirm(true)}
                        disabled={Boolean(busy)}
                      >
                        <FiTrash2 size={13} />
                        {en ? 'Delete' : '삭제'}
                      </button>
                    ) : (
                      user && (
                        <button
                          onClick={() =>
                            setSafety({
                              contentType: 'ALBUM_REVIEW',
                              contentId: review.id,
                              reportedUserId: review.user.id,
                              displayName: review.user.nickname,
                            })
                          }
                        >
                          <FiFlag size={13} />
                          {en ? 'Report' : '신고'}
                        </button>
                      )
                    )}
                  </div>
                </div>
                <div>
                  <div className="pp-review-actions">
                    <button
                      aria-pressed={Boolean(review.isLiked)}
                      disabled={Boolean(busy)}
                      onClick={() =>
                        run('like', async () => {
                          await (review.isLiked
                            ? reviewsApi.deleteLike(review.id)
                            : reviewsApi.addLike(review.id));
                          assertAccount();
                          if (!review.isLiked)
                            trackContentLike(
                              {
                                contentType: CONTENT_TYPE.REVIEW,
                                contentId: review.id,
                                pageType: PAGE_TYPE.REVIEWS_POST,
                              },
                              user,
                            );
                        })
                      }
                    >
                      <FiHeart size={19} fill={review.isLiked ? 'currentColor' : 'none'} />
                      {en ? 'Like' : '좋아요'}
                    </button>
                    <button
                      onClick={() => {
                        commentInput.current?.scrollIntoView({ block: 'center' });
                        commentInput.current?.focus({ preventScroll: true });
                      }}
                    >
                      <FiMessageCircle size={19} />
                      {en ? 'Comments' : '댓글'}
                    </button>
                    <button
                      aria-pressed={Boolean(review.isBookmarked)}
                      disabled={Boolean(busy)}
                      onClick={() =>
                        run('bookmark', () =>
                          review.isBookmarked
                            ? reviewsApi.deleteReviewBookmark(review.id)
                            : reviewsApi.addReviewBookmark(review.id),
                        )
                      }
                    >
                      <FiBookmark size={19} fill={review.isBookmarked ? 'currentColor' : 'none'} />
                      {en ? 'Save' : '저장'}
                    </button>
                    <button disabled={Boolean(busy)} onClick={share}>
                      <FiShare size={19} />
                      {en ? 'Share' : '공유'}
                    </button>
                  </div>
                  <Link className="pp-review-all-link" to={routes.list}>
                    {en ? 'View all member reviews' : '회원 리뷰 전체 보기'}
                    <FiChevronRight size={14} />
                  </Link>
                </div>
                {error && (
                  <p role="alert">
                    {en
                      ? 'Could not complete. Please try again.'
                      : '처리하지 못했어요. 다시 시도해 주세요.'}
                  </p>
                )}
                {notice && <p role="status">{notice}</p>}
              </section>
              <ReviewComments
                reviewId={review.id}
                {...{ en, user, assertAccount }}
                onLogin={login}
                onReport={setSafety}
                onCount={setCommentCount}
                inputRef={commentInput}
                onLoadingChange={setCommentsLoading}
                targetId={initialComment.current}
              />
              {editing && (
                <AlbumActionEditor
                  album={{ ...album, userRating: review.rating, myReview: review }}
                  review={review}
                  mode="review"
                  en={en}
                  onClose={() => {
                    setEditing(false);
                    setEditorState({ dirty: false, busy: false });
                  }}
                  onSave={save}
                  onDelete={remove}
                  onStateChange={setEditorState}
                />
              )}
              {confirm && (
                <AlbumActionConfirm
                  kind="review"
                  hasReview
                  en={en}
                  busy={busy === 'delete'}
                  error={error}
                  onCancel={() => {
                    if (!pending.current) setConfirm(false);
                  }}
                  onConfirm={() => run('delete', remove)}
                />
              )}
              {safety && (
                <ReviewSafetyDialog
                  key={`${safety.contentType}:${safety.contentId}`}
                  target={safety}
                  {...{ en, assertAccount }}
                  onClose={() => setSafety(null)}
                  onNotice={setNotice}
                  onBlocked={() => {
                    void client.invalidateQueries({ queryKey: ['reviewComments'] });
                    void client.invalidateQueries({ queryKey: ['albumReviews'] });
                    releaseDrafts();
                    navigate(routes.album, { replace: true });
                  }}
                />
              )}
            </>
          )}
        </AlbumLoadState>
      </div>
    </PageLayout>
  );
}
